import { useState, type ReactElement } from 'react';
import { useTranslation } from 'react-i18next';
import { Plus } from 'lucide-react';
import type { Task } from '@tailoring-crm/shared';
import { useTasks, type ActivitySubject } from '../../lib/activity/use-activity.js';
import { TaskDialog } from '../activity/task-dialog.js';
import { TaskList } from '../activity/task-list.js';
import { Button, ListSkeleton } from '@tailoring-crm/ui';

/**
 * A customer's follow-ups — the tasks that say when to get in touch next.
 *
 * A follow-up is a task wherever the person sits in the book, so "ring her Friday about the
 * fitting" is made here — for a walk-in as much as for somebody with no orders yet. Ticking one
 * off completes it; the server recomputes the customer's next follow-up date, which the
 * Follow-ups page and the header bell read.
 */
export function CustomerFollowUpsTab({ customerId }: { customerId: string }): ReactElement {
  const { t } = useTranslation();
  const subject: ActivitySubject = { kind: 'customer', id: customerId };
  const tasks = useTasks(subject);
  const [dialog, setDialog] = useState<{ open: boolean; task: Task | null }>({
    open: false,
    task: null,
  });

  return (
    <div className="flex flex-col gap-6">
      <section className="activity-panel flex flex-col gap-4 border p-4">
        <div className="flex flex-wrap items-center justify-between gap-3">
          <h3 className="text-sm font-semibold">{t('activity.tasks.heading')}</h3>
          <Button variant="outline" size="sm" onClick={() => setDialog({ open: true, task: null })}>
            <Plus aria-hidden="true" className="size-4" />
            {t('activity.tasks.new')}
          </Button>
        </div>

        {tasks.isPending ? (
          <ListSkeleton rows={3} />
        ) : (
          <TaskList
            subject={subject}
            tasks={tasks.data?.items ?? []}
            readOnly={false}
            onEdit={(task) => setDialog({ open: true, task })}
          />
        )}
      </section>

      <TaskDialog
        open={dialog.open}
        onOpenChange={(open) => setDialog((previous) => ({ ...previous, open }))}
        subject={subject}
        task={dialog.task}
      />
    </div>
  );
}
